import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, money, inputCls } from '../organization/finance/shared';
import { PERIOD_OPTIONS } from './periodOptions';

const Metric = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="text-xs font-semibold text-slate-500">{label}</p>
    <p className="mt-1 text-xl font-black text-slate-900">{value}</p>
  </div>
);

const TopList = ({ title, items, testId }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testId}>
    <p className="mb-3 text-sm font-bold text-slate-700">{title}</p>
    {(!items || items.length === 0) ? (
      <p className="text-xs text-slate-400">لا توجد بيانات كافية</p>
    ) : (
      <div className="space-y-1.5">
        {items.map((it, i) => (
          <div key={i} className="flex items-center justify-between text-sm">
            <span className="text-slate-700">{it.label}</span>
            <span className="font-bold text-slate-900">{it.count}</span>
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function VenueOwnerReportsPage() {
  const [period, setPeriod] = useState('this_month');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (period === 'custom') { params.date_from = customFrom; params.date_to = customTo; }
      const res = await axios.get(`${API}/venues/owner/reports`, { ...auth(), params });
      setReport(res.data);
    } catch { setReport(null); } finally { setLoading(false); }
  }, [period, customFrom, customTo]);

  useEffect(() => { if (period !== 'custom' || (customFrom && customTo)) load(); }, [load, period, customFrom, customTo]);

  return (
    <div className="space-y-4" data-testid="venue-owner-reports-page">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 bg-white p-3">
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className={`${inputCls} w-auto`} data-testid="venue-owner-reports-period-select">
          {PERIOD_OPTIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {period === 'custom' && (
          <>
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className={`${inputCls} w-auto`} data-testid="venue-owner-reports-custom-from" />
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className={`${inputCls} w-auto`} data-testid="venue-owner-reports-custom-to" />
          </>
        )}
      </div>

      {loading || !report ? (
        <div className="h-40 animate-pulse rounded-2xl bg-slate-100" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Metric label="عدد الحجوزات" value={report.total_bookings} testId="venue-owner-report-total-bookings" />
            <Metric label="نسبة الإشغال (تقديرية)" value={`${report.occupancy_percent}%`} testId="venue-owner-report-occupancy" />
            <Metric label="الإيرادات المتوقعة" value={money(report.expected_revenue)} testId="venue-owner-report-revenue" />
            <Metric label="ملغاة/مرفوضة" value={report.rejected_count} testId="venue-owner-report-rejected" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <TopList title="أفضل الأيام" items={report.top_days} testId="venue-owner-report-top-days" />
            <TopList title="أفضل الساعات" items={report.top_hours} testId="venue-owner-report-top-hours" />
            <TopList title="أكثر الكورتات حجزاً" items={report.top_courts} testId="venue-owner-report-top-courts" />
          </div>
        </>
      )}
    </div>
  );
}
